import { clsx } from "clsx";
import pluralize from "pluralize";
import { flatten } from "ramda";
import { type FC, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMatch } from "react-router";

import { LoadingSpinner, PanelGroup, PanelOrientation, PanelPosition } from "widgets";

import styleLayout from "../core/styles/PageLayout.module.scss";

import { useURLViewID } from "../../app-utils/hooks/useUrl.js";
import { capitalizeFirstLetter } from "../../app-utils/string.js";
import { type ListingMapEntry, type OrganismsInfo } from "../../graphql.js";
import {
  type IContentBrowserState,
  VISIBLE_ORGANISM_TABLE_FIELDS_BY_ATTRIBUTE,
  changeGroupByAttributeFilter,
  changeStatisticsFilter,
  createContentBrowserTableSpec,
  loadContentBrowserInfo,
  loadContentBrowserListing,
} from "../../redux/modules/content-browser.js";
import { type SavedViewState, loadView } from "../../redux/modules/saved-view.js";
import { type ITableData, type ITableInitializationOptions, getTableDataKey } from "../../redux/modules/table-data.js";
import { type IAppState } from "../../redux/reducer.js";
import { type ReduxDispatch } from "../../redux/redux-utils.js";
import {
  ENTITY_TYPE,
  GROUP_BY_ATTRIBUTE_SEQUENCING_TYPE,
  GROUP_BY_ATTRIBUTE_VARIANT_ANALYSIS_GROUP,
  GROUP_BY_TAXONOMIC_GROUP,
  ORGANISM_TABLE_COLUMNS,
} from "../../settings/settings.js";
import { ContentPlaceholder } from "../core/ContentPlaceholder.js";
import { ContentBrowserButtonHeader } from "../core/navigation-panel/ButtonHeaderHoc.js";
import PageHeader from "../core/navigation-panel/PageHeader.js";
import ContentBrowserChartView from "./ContentBrowserChart.js";
import { CONTENT_BROWSER_FEATURES } from "./ContentBrowserPage.js";
import ContentBrowserTableView from "./ContentBrowserTableView.js";

// :featureType is always "organisms", check Routes
const FEATURE_TYPE = "organisms";

/**
 * Content Browser main component
 */
const ContentBrowserGenomesSubPage: FC = () => {
  const dispatch = useDispatch<ReduxDispatch>();
  const viewId = useURLViewID();
  const match = useMatch("/content/:featureType/:groupByAttribute");
  const { view, isLoading: isSavedViewLoading } = useSelector<IAppState, SavedViewState>((state) => state.savedView);
  const { content, selectedEntry, isLoadingListing, isLoadingInfo } = useSelector<IAppState, IContentBrowserState>(
    (state) => state.contentBrowser,
  );
  const tableData = useSelector<IAppState, ITableData>(
    (state) => state.tableData.tables[getTableDataKey(createContentBrowserTableSpec(FEATURE_TYPE))],
  );

  // Selection in the left NavigationPanel (Key)
  const selectedContentBrowserAttribute = useMemo(
    () =>
      CONTENT_BROWSER_FEATURES[0].navItems.find((subroute) => subroute.eventKey === match?.params.groupByAttribute)
        ?.eventKey,
    [match?.params.groupByAttribute],
  );

  useEffect(() => {
    if (!isSavedViewLoading && !!viewId) {
      if (!(view && Number.parseInt(view?.id) === viewId)) {
        // correct view is NOT loaded
        // initialization of info, listing and counts happens when loading the view is done
        // new view loading mechanism allows to load the same view from multiple components
        dispatch(loadView(viewId));
      }
    }
  }, [view, isSavedViewLoading, viewId, dispatch]);

  // triggered when the attribute is modified (the nav panel is clicked)
  useEffect(() => {
    if (selectedEntry) {
      dispatch(changeGroupByAttributeFilter(null)); // reset selected entry upon menu change
    }
    dispatch(loadContentBrowserListing(FEATURE_TYPE, selectedContentBrowserAttribute)).then(() =>
      dispatch(loadContentBrowserInfo(FEATURE_TYPE, selectedContentBrowserAttribute, null)),
    );
  }, [dispatch, selectedContentBrowserAttribute]);

  // Triggered when a group in the info chart is clicked
  useEffect(() => {
    let filterKey = selectedEntry?.key;
    // special case for the Genome Type for which we give the label
    if (selectedContentBrowserAttribute === GROUP_BY_ATTRIBUTE_SEQUENCING_TYPE) {
      filterKey = selectedEntry?.label;
    }
    dispatch(loadContentBrowserInfo(FEATURE_TYPE, selectedContentBrowserAttribute, filterKey));
    // Adding selectedContentBrowserAttribute to the dependencies
    // causes exceptions when navigating in the content browser
  }, [dispatch, selectedEntry]);

  function getPlaceholderText(listing: ListingMapEntry[]) {
    if (!isLoadingListing && !isLoadingInfo) {
      let placeholderTextCategoryLabel: string | undefined;
      // some category labels will differ between the side menu and the placeholder text (see comments in SEL6-566)
      switch (selectedContentBrowserAttribute) {
        case GROUP_BY_ATTRIBUTE_VARIANT_ANALYSIS_GROUP:
          placeholderTextCategoryLabel = !listing || listing.length === 0 ? "Genome Analysis Groups" : undefined;
          break;
        case GROUP_BY_TAXONOMIC_GROUP:
          placeholderTextCategoryLabel = !listing || listing.length <= 1 ? "Taxonomy Groups" : undefined;
          break; // as discussed in SEL6-891, one single Taxonomy Group is same as none
        default:
          placeholderTextCategoryLabel =
            !listing || listing.length === 0
              ? CONTENT_BROWSER_FEATURES[0].navItems.find((r) => r.eventKey === selectedContentBrowserAttribute)?.label
              : undefined;
      }

      return (
        placeholderTextCategoryLabel && (
          <span>
            Available genomes are not currently organized by {pluralize(placeholderTextCategoryLabel.toLowerCase())}.
            <br />
            <br />
            To see genomes listed here, ask your system administrator for assistance.
          </span>
        )
      );
    }

    return null;
  }

  const getTableInitializationOptions = (): ITableInitializationOptions => {
    return {
      visibleColumns: VISIBLE_ORGANISM_TABLE_FIELDS_BY_ATTRIBUTE[selectedContentBrowserAttribute],
      sortExpression: ORGANISM_TABLE_COLUMNS.NAME,
    };
  };

  const featureContent = content?.[FEATURE_TYPE];
  const groupByContent = featureContent?.[selectedContentBrowserAttribute];
  const listing: ListingMapEntry[] = featureContent && groupByContent?.listing;
  const organismsInfo: OrganismsInfo = featureContent && groupByContent?.info;
  const placeHolderText = getPlaceholderText(listing);

  return (
    <>
      <PageHeader
        title={capitalizeFirstLetter(CONTENT_BROWSER_FEATURES?.[0]?.label)}
        subtitle={
          capitalizeFirstLetter(
            CONTENT_BROWSER_FEATURES[0].navItems.find((r) => r.eventKey === selectedContentBrowserAttribute)?.label,
          ) + " Overview"
        }
        badge={String(featureContent?.count ?? 0)}
        actionWidgets={
          !(isSavedViewLoading || placeHolderText)
            ? flatten([
                <ContentBrowserButtonHeader
                  entityType={ENTITY_TYPE.ORGANISM}
                  key={"content-browser-table-view-action-2"}
                />,
              ])
            : undefined
        }
        isLoading={isSavedViewLoading}
        setDocumentTitle
      />
      <div className={styleLayout.page}>
        {isSavedViewLoading ? (
          <LoadingSpinner inCenter />
        ) : placeHolderText ? (
          <ContentPlaceholder text={placeHolderText} />
        ) : (
          <PanelGroup
            defaultSize={252}
            minSize={200}
            className={"content-browser"}
            isContinuous
            fixedPanel={PanelPosition.Leading}
            orientation={PanelOrientation.Vertical}
          >
            <div className={clsx(styleLayout.grid_pane)}>
              <ContentBrowserChartView
                key="content-browser-chart-view"
                isLoadingListing={tableData?.isLoadingTableMeta || isLoadingListing}
                isLoadingInfo={tableData?.isLoadingTableMeta || isLoadingInfo}
                listing={listing}
                organismsInfo={organismsInfo}
                selectedEntry={selectedEntry}
                selectedContentBrowserFeature={CONTENT_BROWSER_FEATURES[0]}
                selectedContentBrowserAttribute={CONTENT_BROWSER_FEATURES[0].navItems.find(
                  (r) => r.eventKey === selectedContentBrowserAttribute,
                )}
                totalCount={featureContent?.count}
                changeGroupByAttributeFilter={(entry) => dispatch(changeGroupByAttributeFilter(entry))}
                changeStatisticsFilter={(columnField, filter) => dispatch(changeStatisticsFilter(columnField, filter))}
              />
            </div>
            <div className={clsx(styleLayout.grid_pane)}>
              <ContentBrowserTableView
                selectedContentBrowserFeature={CONTENT_BROWSER_FEATURES[0]}
                selectedContentBrowserAttribute={CONTENT_BROWSER_FEATURES[0].navItems.find(
                  (r) => r.eventKey === selectedContentBrowserAttribute,
                )}
                getTableInitializationOptions={getTableInitializationOptions}
                viewId={viewId}
                selectedEntityType={ENTITY_TYPE.ORGANISM}
              />
            </div>
          </PanelGroup>
        )}
      </div>
    </>
  );
};

export default ContentBrowserGenomesSubPage;
